import { useAtomValue, useSetAtom } from "jotai";
import { Suspense, useEffect, useState } from "react";
import { PreloadedQuery } from "react-relay";

import { useSetValidTransportationTypeQuery } from "@/features/BookingWizard/components/WizardStepContainer/steps/AddPlayersStep/hooks/__generated__/useSetValidTransportationTypeQuery.graphql";
import { getRoundFromIdAtom } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/atoms/selectors/getRoundFromIdAtom/getRoundFromIdAtom";
import { isAlreadyPaidAtom } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/atoms/selectors/isAlreadyPaidAtom/isAlreadyPaidAtom";
import {
  currentBookingAtom,
  currentSessionUserClientIdAtom,
} from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/atoms/stores";
import {
  DropdownSelect,
  DropdownSelectOption,
} from "@whoosh/design-system/src/components/controls";
import { SkeletonLoader } from "@whoosh/design-system/src/components/display";
import { GetTransportIconFromCode } from "@whoosh/design-system/src/components/icons_v2/transportation";
import { VStack } from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";

import { setTransportationTypeOnRoundAtom } from "../../../../../../../../../../atoms/actions/setTransportationTypeOnRound/setTransportationTypeOnRoundAtom";
import { getTargetTransportationRoundIds } from "../hooks/getTargetTransportationRoundIds";
import {
  useRoundTransportationTypes,
  useRoundTransportationTypesFromQuery,
} from "../hooks/useRoundTransportationTypes";
import { getTransportationTypeFromRoundIdAtom } from "./selectors/getTransportationTypeFromRoundIdAtom";

type Props = {
  roundId: string;
  shouldFillWidth?: boolean;
};
export const TransportationTypeControl = ({
  roundId,
  shouldFillWidth = false,
}: Props) => {
  const [isTargetingAll, setIsTargetingAll] = useState(false);
  const currentBooking = useAtomValue(currentBookingAtom);
  const currentRound = useAtomValue(getRoundFromIdAtom(roundId));
  const currentSessionUserClientId = useAtomValue(
    currentSessionUserClientIdAtom
  );
  const { queryRef } = useRoundTransportationTypes({
    slotId: currentBooking?.startSlotId,
    round: currentRound,
    currentSessionUserClientId,
  });

  if (!queryRef) {
    return (
      <VStack
        flex={shouldFillWidth ? 1 : undefined}
        width={shouldFillWidth ? undefined : 108}
      >
        <SkeletonLoader
          borderRadius="2"
          width={shouldFillWidth ? "100%" : 108}
          height={48}
        />
      </VStack>
    );
  }

  return (
    <Suspense
      fallback={
        <VStack
          flex={shouldFillWidth ? 1 : undefined}
          width={shouldFillWidth ? undefined : 108}
        >
          <SkeletonLoader
            borderRadius="2"
            width={shouldFillWidth ? "100%" : 108}
            height={48}
          />
        </VStack>
      }
    >
      <TransportationTypeControlInner
        queryRef={queryRef}
        roundId={roundId}
        isTargetingAll={isTargetingAll}
        setIsTargetingAll={setIsTargetingAll}
        shouldFillWidth={shouldFillWidth}
      />
    </Suspense>
  );
};

type InnerProps = {
  queryRef: PreloadedQuery<useSetValidTransportationTypeQuery>;
  roundId: string;
  isTargetingAll: boolean;
  setIsTargetingAll: (value: boolean) => void;
  shouldFillWidth: boolean;
};

const TransportationTypeControlInner = ({
  queryRef,
  roundId,
  isTargetingAll,
  setIsTargetingAll,
  shouldFillWidth,
}: InnerProps) => {
  const currentBooking = useAtomValue(currentBookingAtom);
  const { currencySymbol } = useGetClubCurrency();
  const currentRound = useAtomValue(getRoundFromIdAtom(roundId));
  const permittedTransportationTypes =
    useRoundTransportationTypesFromQuery(queryRef);
  const shouldHidePrices = !!currentBooking?.isPrivate;
  const selectedTransportationTypeId = useAtomValue(
    getTransportationTypeFromRoundIdAtom(roundId)
  );
  const setTransportationTypeOnRound = useSetAtom(
    setTransportationTypeOnRoundAtom
  );
  const targetTransportationRoundIds = getTargetTransportationRoundIds({
    booking: currentBooking,
    currentRound,
    isTargetingAll,
  });
  const dropdownOptions: DropdownSelectOption<string>[] =
    permittedTransportationTypes.flatMap((tt) => {
      if (!tt || !tt?.id) return [];
      const icon = GetTransportIconFromCode(tt.code ?? null);
      const priceInCents = tt.eighteenHolePrice ?? 0;
      const priceDisplay = ConvertCentsToDollars(priceInCents);
      return {
        value: tt.id,
        leftElement: icon ? icon({}) : undefined,
        label: tt.name || "",
        subLabel: shouldHidePrices ? "" : `${currencySymbol}${priceDisplay}`,
      };
    });

  const isAlreadyPaid = useAtomValue(isAlreadyPaidAtom);

  useEffect(() => {
    const firstTransportationTypeId = dropdownOptions[0]?.value;
    const transportationTypeStillAllowed = dropdownOptions.some(
      (option) => option.value === selectedTransportationTypeId
    );

    if (
      firstTransportationTypeId &&
      (!selectedTransportationTypeId || !transportationTypeStillAllowed)
    ) {
      setTransportationTypeOnRound({
        roundId,
        transportationTypeId: firstTransportationTypeId,
        isTargetingAll: false,
      });
    }
  }, [
    dropdownOptions,
    roundId,
    selectedTransportationTypeId,
    setTransportationTypeOnRound,
  ]);

  return (
    <VStack
      flex={shouldFillWidth ? 1 : undefined}
      width={shouldFillWidth ? undefined : 108}
    >
      <DropdownSelect
        testID="booking-transportation-dropdown"
        hideOuterMargin
        hideChevron
        hideLabelInDisplay={!shouldFillWidth}
        includeLeftElementInSelectedValue
        optionalToggle={{
          initiallyChecked: isTargetingAll,
          onToggle: setIsTargetingAll,
        }}
        isViewOnly={isAlreadyPaid}
        contentSizeOverride="regular"
        options={dropdownOptions}
        value={selectedTransportationTypeId ?? dropdownOptions[0]?.value}
        onChange={(transportationTypeId) => {
          setTransportationTypeOnRound({
            roundId,
            transportationTypeId: transportationTypeId ?? "",
            isTargetingAll,
            targetRoundIds: targetTransportationRoundIds,
          });
        }}
      />
    </VStack>
  );
};
